import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';

interface ImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: React.ReactNode;
  /**
   * Marca a imagem como prioritária (acima da dobra / primeiras do catálogo).
   * Usa loading="eager" + fetchPriority="high" para o Android não adiar o
   * download. Deixe false (default) para imagens fora da tela.
   */
  priority?: boolean;
}

const Img: React.FC<ImgProps> = React.memo(({ src, alt, fallback, className, priority = false, ...props }) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  //* Guarda se a imagem já estava pronta no cache no primeiro paint. Nesse
  //* caso NÃO aplicamos o fade (evita atraso perceptível, especialmente no
  //* Android com muitas imagens de uma vez).
  const [instant, setInstant] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  //* src já resolvido pelo <img> na última sincronização. Evita resetar o
  //* estado (e voltar ao skeleton) em re-renders que não trocam a imagem.
  const syncedSrcRef = useRef<string | undefined>(undefined);

  /**
   * Sincroniza o estado com o carregamento real da imagem.
   *
   * Corrige a corrida do <img> no Android WebView: ao recarregar com a imagem
   * já no cache, o load pode terminar ANTES de o React anexar o onLoad — o
   * evento nunca dispara e a imagem ficaria presa em opacity-0 (skeleton
   * eterno). Aqui verificamos img.complete no commit para resolver esse caso.
   *
   * Só reseta o estado quando o src realmente muda (syncedSrcRef), evitando
   * flicker/atraso quando o componente re-renderiza com o mesmo src (ex.: o
   * Swiper dos banners recalculando slidesPerView).
   */
  useLayoutEffect(() => {
    const img = imgRef.current;
    const srcChanged = syncedSrcRef.current !== src;

    if (srcChanged) {
      syncedSrcRef.current = src;
      setError(false);
      setLoaded(false);
      setInstant(false);
    }

    if (img && img.complete) {
      if (img.naturalWidth > 0) {
        setLoaded(true);
        //* Sem fade quando já veio do cache neste paint.
        if (srcChanged) setInstant(true);
      } else if (img.naturalWidth === 0 && srcChanged) {
        setError(true);
      }
    }
  }, [src]);

  const handleLoad = useCallback(() => setLoaded(true), []);
  const handleError = useCallback(() => setError(true), []);

  if (error && fallback) return <>{fallback}</>;

  return (
    <div className={`relative overflow-hidden ${className}`}>

      {!loaded && !error && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse" />
      )}

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} ${instant ? '' : 'transition-opacity duration-200'}`}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? 'eager' : 'lazy'}
        //* fetchPriority ajuda o Android a priorizar as imagens visíveis.
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async" //* Evita que a decodificação da imagem bloqueie a thread principal do navegador.
        {...props}
      />
    </div>
  );
});

export default Img;
